import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useState } from 'react';

import { getLeadByExternalIdQueryKey } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePostLeadByExternalIdCalcDecision } from '../../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdCalcDecision';
import { GetLeadByExternalId200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { devGuidanceScenarioAtom } from '../../../devToolbarAtoms';
import { GateDecision } from '../../guidance';
import { getOfferErrorMessage } from '../offers/utils';

type GateDecisionState = {
  record: (decision: GateDecision) => Promise<boolean>;
  pendingDecision: GateDecision | null;
  errorMessage: string | null;
};

// list_anyway activates the listing server-side in this same call; the merged response
// flips the cached status so the dashboard re-renders without waiting on the poll.
const useGateDecision = (externalId: string): GateDecisionState => {
  const queryClient = useQueryClient();
  const setDevGuidanceScenario = useSetAtom(devGuidanceScenarioAtom);
  const [pendingDecision, setPendingDecision] = useState<GateDecision | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutateAsync: postDecision } = usePostLeadByExternalIdCalcDecision();

  const record = async (decision: GateDecision) => {
    setPendingDecision(decision);
    setErrorMessage(null);
    try {
      const response = await postDecision({ externalId, data: { decision } });
      queryClient.setQueryData<GetLeadByExternalId200 | undefined>(
        getLeadByExternalIdQueryKey({ externalId }),
        (cached) =>
          cached && {
            ...cached,
            status: response.status as GetLeadByExternalId200['status'],
            listVsBuyout: response.listVsBuyout,
            gateDecision: response.gateDecision,
            gateDecidedAt: response.gateDecidedAt,
          },
      );
      queryClient.invalidateQueries({ queryKey: getLeadByExternalIdQueryKey({ externalId }) });
      setDevGuidanceScenario('');
      return true;
    } catch (error) {
      setErrorMessage(
        getOfferErrorMessage(error) ?? "We couldn't save your choice. Please try again.",
      );
      return false;
    } finally {
      setPendingDecision(null);
    }
  };

  return { record, pendingDecision, errorMessage };
};

export default useGateDecision;
